'use strict';

/*
  Secret Gifters — Portaal Secure Mode

  Wordt geladen NA portaal.js wanneer in theme settings `portaal_use_proxy`
  aanstaat. Overschrijft de globale functies fetchAirtable / createAirtableRecord /
  updateAirtableRecord / deleteAirtableRecord / postWebhook zodat alle calls
  via Shopify App Proxy gaan in plaats van rechtstreeks naar Airtable.

  Doel: de Airtable-token, CRM API keys en Make.com-webhook-URL's verlaten
  nooit meer de server. De browser ziet alleen /apps/portaal-api/* (zelfde
  origin als de winkel, automatisch ondertekend door Shopify).

  Nadat dit script geladen is, blijven alle bestaande aanroepen in portaal.js
  werken zonder verdere aanpassingen — de wrappers vertalen de oude API.
*/

/*
  Wacht tot portaal.js zijn globals heeft gedefinieerd, dan pas overschrijven.
  We hooken op `DOMContentLoaded` met een microtask delay; portaal.js wordt
  via {{ 'portaal.js' | asset_url | script_tag }} normaal geladen (sync) en
  staat klaar tegen die tijd.
*/
function _portaalSecureBoot() {
  if (typeof window === 'undefined') return;

  // Basis-pad van de App Proxy. Wordt door portaal-config.liquid gezet,
  // standaard /apps/portaal-api.
  const API_BASE =
    (window.PORTAAL_CONFIG && window.PORTAAL_CONFIG.apiBase) ||
    '/apps/portaal-api';

  /*
    Mapping: oude Airtable table-naam → proxy endpoint-pad.
    Tabellen die niet hier staan, gaan NIET via de proxy en zullen falen.
    Dat is bedoeld: zo merkt de developer direct dat ze een nieuwe table
    server-side moeten toevoegen, in plaats van stilletjes door te gaan.
  */
  const TABLE_TO_ENDPOINT = {
    'Medewerkers': '/medewerkers',
    'Geplande Momenten': '/momenten',
    'Maandfacturen': '/facturen',
    'Klanten': '/bedrijfsinfo',
    'Producten': '/producten',
    'Portaal Gebruikers': '/sub-gebruikers',
    'Documenten': '/documenten',
  };

  /*
    Mapping: oude Make webhook → proxy endpoint.
    Sommige webhooks zijn nu volledig server-side (CSV-import doet alles
    direct in Airtable), andere zijn een dunne forward naar Make.
  */
  const WEBHOOK_KEY_TO_ENDPOINT = {
    medewerkerToevoegen: '/medewerkers',         // POST → wordt direct opgeslagen
    csvImport:           '/medewerkers-csv',
    momentToevoegen:     '/momenten',
    bedrijfsinfoOpslaan: '/bedrijfsinfo',
    accountOpzeggen:     '/account/opzeggen',
    upgradeNaarGuided:   '/account/upgrade-naar-guided',
    gebruikerUitnodigen: '/sub-gebruikers/uitnodigen',
    crmSync:             '/crm/sync',
    emailWijzigen:       '/account/email-wijzigen',
  };

  // Admin-impersonation token uit localStorage. Zodra een admin via de
  // SG Admin-bar een klant overneemt, wordt hier een token bewaard. Voegen
  // we toe als header zodat de server in elk request weet welke klant
  // gecontextualiseerd is. Headers gaan betrouwbaar via Shopify App Proxy.
  function impersonationToken() {
    try { return localStorage.getItem('sg_admin_acting_as') || null; }
    catch { return null; }
  }

  async function apiFetch(path, init = {}) {
    const url = API_BASE.replace(/\/$/, '') + path;
    const headers = {
      'Accept': 'application/json',
      ...(init.headers || {}),
    };
    const token = impersonationToken();
    if (token) headers['X-Sg-Impersonate'] = token;

    const res = await fetch(url, {
      credentials: 'same-origin',
      ...init,
      headers,
    });
    let data = null;
    try { data = await res.json(); } catch { /* niet-JSON */ }
    if (!res.ok) {
      const err = new Error((data && data.error) || `HTTP ${res.status}`);
      err.status = res.status;
      err.body = data;
      throw err;
    }
    return data;
  }

  function endpointForTable(table) {
    const ep = TABLE_TO_ENDPOINT[table];
    if (!ep) {
      console.warn('[portaal-secure] onbekende tabel via proxy:', table);
    }
    return ep;
  }

  /*
    Drop-in vervanger voor fetchAirtable. De oude signature was:
      fetchAirtable(table, filterFormula, sortField, sortDir)
    De server doet zelf klant-filtering, dus we negeren filterFormula —
    behalve als hij specifiek vraagt om een ander record (bv. zoeken op id).
  */
  /*
    Foutbanner. Voorheen slikte fetchAirtable elke fout in en gaf [] terug,
    waardoor een 401 (sessie niet herkend) of een serverfout er in de UI
    precies zo uitzag als "je hebt nog geen medewerkers". Klanten dachten
    dan dat hun gegevens weg waren en niemand kon zien wat er misging
    (Ekco, sept 2026). Nu: één zichtbare banner per pagina, met bij een 401
    een directe link om opnieuw in te loggen op dezelfde pagina.
  */
  window._PORTAAL_LAATSTE_FOUT = null;

  function toonApiFoutBanner(err, path) {
    const status = err && err.status;
    window._PORTAAL_LAATSTE_FOUT = {
      status: status || null,
      path,
      message: (err && err.message) || 'Onbekende fout',
      at: Date.now(),
    };

    const main = document.querySelector('.portaal-main');
    if (!main || document.getElementById('portaal-api-fout-banner')) return;

    let tekst;
    if (status === 401) {
      const terug = encodeURIComponent(window.location.pathname);
      tekst = 'Je sessie is verlopen of wordt niet herkend, daardoor kunnen je gegevens niet geladen worden. '
        + '<a href="/customer_authentication/login?return_to=' + terug + '" style="color:#1e40af;text-decoration:underline;font-weight:600;">Log opnieuw in</a>.';
    } else if (status === 423) {
      tekst = 'Je klantprofiel wordt nog aangemaakt. Ververs de pagina over een minuut.';
    } else if (status) {
      tekst = 'De gegevens konden niet geladen worden (fout ' + status + '). Ververs de pagina of probeer het later opnieuw. '
        + 'Blijft dit gebeuren? Mail <a href="mailto:info@secretgifters.nl" style="color:#1e40af;text-decoration:underline;">info@secretgifters.nl</a>.';
    } else {
      tekst = 'Geen verbinding met de server. Controleer je internetverbinding en ververs de pagina.';
    }

    const banner = document.createElement('div');
    banner.id = 'portaal-api-fout-banner';
    banner.setAttribute('role', 'alert');
    banner.style.cssText = 'display:flex;align-items:center;gap:10px;background:#FEF2F2;border:1px solid #FECACA;'
      + 'border-radius:10px;padding:12px 16px;margin-bottom:20px;color:#7f1d1d;font-size:0.95rem;line-height:1.4;';
    banner.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;color:#dc2626;"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg><span>' + tekst + '</span>';
    main.insertAdjacentElement('afterbegin', banner);
  }

  window.fetchAirtable = async function (table, filterFormula, sortField, sortDir) {
    const ep = endpointForTable(table);
    if (!ep) return [];

    const params = new URLSearchParams();
    if (sortField) params.set('sort', sortField);
    if (sortDir) params.set('direction', sortDir);
    const qs = params.toString();
    window._PORTAAL_LAATSTE_FOUT = null;
    try {
      const data = await apiFetch(`${ep}${qs ? `?${qs}` : ''}`);
      // Server geeft { records: [...] } voor lijst-endpoints; { record: {...} }
      // voor enkelvoud (bedrijfsinfo). Beide verpakken naar Airtable-stijl.
      if (data && Array.isArray(data.records)) {
        return data.records.map((r) => ({ id: r.id, fields: stripIdField(r) }));
      }
      if (data && data.record) {
        return [{ id: data.record.id, fields: stripIdField(data.record) }];
      }
      return [];
    } catch (err) {
      if (err.status === 403 && ep === '/sub-gebruikers') {
        // Medegebruikers mogen de gebruikerslijst niet zien; dat is
        // bedoeld gedrag, geen storing. Stil [] teruggeven.
        return [];
      }
      console.error('[portaal-secure] fetchAirtable mislukt:', ep, err.status || '', err.message);
      toonApiFoutBanner(err, ep);
      return [];
    }
  };

  window.createAirtableRecord = async function (table, fields) {
    const ep = endpointForTable(table);
    if (!ep) throw new Error('Onbekende tabel');
    const data = await apiFetch(ep, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
    });
    if (data && data.record) {
      return { id: data.record.id, fields: stripIdField(data.record) };
    }
    return data;
  };

  window.updateAirtableRecord = async function (table, recordId, fields) {
    const ep = endpointForTable(table);
    if (!ep) throw new Error('Onbekende tabel');
    // Voor /bedrijfsinfo is er geen record-id in het pad — daar is de klant
    // zelf het record.
    const path = ep === '/bedrijfsinfo' ? ep : `${ep}/${encodeURIComponent(recordId)}`;
    const data = await apiFetch(path, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
    });
    if (data && data.record) {
      return { id: data.record.id, fields: stripIdField(data.record) };
    }
    return data;
  };

  window.deleteAirtableRecord = async function (table, recordId) {
    const ep = endpointForTable(table);
    if (!ep) return false;
    try {
      await apiFetch(`${ep}/${encodeURIComponent(recordId)}`, { method: 'DELETE' });
      return true;
    } catch (err) {
      console.error('[portaal-secure] delete mislukt:', err.message);
      return false;
    }
  };

  /*
    postWebhook(url, data) wordt door portaal.js aangeroepen met als eerste
    argument de URL. We mappen de URL terug naar een logische webhook-key
    via de configuratie (PORTAAL_CONFIG.makeWebhooks) en sturen door naar
    het bijbehorende proxy-endpoint.

    Voor backwards-compatibility: als de URL leeg is of we de mapping niet
    vinden, vallen we terug op een directe POST (oude gedrag).
  */
  const _origPostWebhook = window.postWebhook;
  window.postWebhook = async function (url, data) {
    const cfg = window.PORTAAL_CONFIG || {};
    const webhooks = cfg.makeWebhooks || {};
    const key = Object.keys(webhooks).find((k) => webhooks[k] && webhooks[k] === url);
    const endpoint = key ? WEBHOOK_KEY_TO_ENDPOINT[key] : null;
    if (!endpoint) {
      if (typeof _origPostWebhook === 'function') return _origPostWebhook(url, data);
      throw new Error('Webhook niet gemapt naar proxy');
    }
    return apiFetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  };

  function stripIdField(rec) {
    const { id, ...rest } = rec;
    return rest;
  }

  // Markeer dat we in secure mode draaien — handig voor debug
  window._PORTAAL_SECURE_MODE = true;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', _portaalSecureBoot);
} else {
  _portaalSecureBoot();
}
